import { SettingsManager } from "@viamap/viamap2-common";
import type { PropertyData } from "src/propertyInfoTemplates/PropertyInfoCollectorTypes";

export const MOLBAK_PRIVACY_POLICY_URL = "https://molbak.dk/om-moelbak/vores-politikker/persondatapolitik/";

export type ServitutMolbakHelpContext = {
   matrikelNr: string;
   ejerlav: string;
   servitutNr: string;
};

export type ServitutHelpMailPayload = ServitutMolbakHelpContext & {
   name: string;
   email: string;
   phone: string;
   message: string;
};

type JordstykkeNode = {
   status?: string;
   matrikelnummer?: string;
   jordstykkeLiggerIEjerlav?: { ejerlavsnavn?: string };
};

type ColumnDetail = {
   matrikler?: string[];
   ejerlav?: string;
};

export function canSubmitServitutHelp(input: {
   consentPrivacy: boolean;
   sending: boolean;
}): boolean {
   return input.consentPrivacy && !input.sending;
}

export function formatServitutHelpNumber(servitut?: {
   aktId?: string;
   aktAliasId?: string;
   dokumentFilnavn?: string;
} | null): string {
   const dato = formatDatoLoebenr(servitut?.aktId || servitut?.aktAliasId);
   return appendDocumentFileName(dato, servitut?.dokumentFilnavn);
}

function appendDocumentFileName(number: string, dokumentFilnavn?: string): string {
   const fileName = dokumentFilnavn?.trim() || "";
   if (number && fileName && fileName !== number) {
      return `${number} (${fileName})`;
   }
   return number || fileName;
}

export function servitutHelpContextFromProperty(
   propertyData: Partial<PropertyData> | undefined,
   servitut?: { aktId?: string; aktAliasId?: string; dokumentFilnavn?: string } | null,
): ServitutMolbakHelpContext {
   const cadastral = cadastralFromProperty(propertyData);
   return {
      ...cadastral,
      servitutNr: formatServitutHelpNumber(servitut),
   };
}

export function servitutHelpContextFromMatrixRow(
   row: {
      datoLoebenr?: string;
      identityLabel?: string;
      displayAktId?: string;
      includedBfes?: number[];
      servitut?: { aktId?: string; aktAliasId?: string; dokumentFilnavn?: string };
   },
   columnDetails?: Record<string, ColumnDetail>,
): ServitutMolbakHelpContext {
   const cadastral = cadastralFromColumns(row.includedBfes, columnDetails);
   const displayed = (row.datoLoebenr || row.identityLabel || row.displayAktId || "").trim();
   const servitutNr = displayed
      ? appendDocumentFileName(displayed, row.servitut?.dokumentFilnavn)
      : formatServitutHelpNumber(row.servitut);
   return { ...cadastral, servitutNr };
}

export function formatServitutHelpMailSubject(payload: ServitutHelpMailPayload): string {
   const servitutNr = payload.servitutNr.trim();
   return servitutNr ? `Hjælp til servitutter: ${servitutNr}` : "Hjælp til servitutter";
}

export function formatServitutHelpMailText(payload: ServitutHelpMailPayload): string {
   return [
      "Hjælp til servitutter",
      "",
      `Matr.nr.: ${payload.matrikelNr.trim()}`,
      `Ejerlav: ${payload.ejerlav.trim()}`,
      `Servitutnr.: ${payload.servitutNr.trim()}`,
      `Navn: ${payload.name.trim()}`,
      `Mail: ${payload.email.trim()}`,
      `Telefon: ${payload.phone.trim()}`,
      "",
      "Besked:",
      payload.message.trim(),
      "",
      "Mølbaks persondatapolitik accepteret: ja",
   ].join("\n");
}

export type ServitutHelpCaller = {
   userRef: string;
   userName: string;
   organizationId: string;
};

export async function sendServitutHelpMail(
   payload: ServitutHelpMailPayload,
   caller: ServitutHelpCaller,
   deps: {
      host?: string;
      token?: string;
      to?: unknown;
      fetch?: typeof fetch;
   } = {},
): Promise<void> {
   const host = (deps.host ?? String(SettingsManager.getSystemSetting("jobs.jobServiceHost", "") || ""))
      .trim()
      .replace(/\/$/, "");
   const token = (deps.token ?? String(SettingsManager.getSystemSetting("jobs.jobServiceAPIKey", "") || "")).trim();
   const to = readServitutHelpRecipients(deps.to ?? SettingsManager.getSystemSetting("jobs.servitutHelpMailTo"));
   const userRef = caller.userRef.trim();
   const userName = caller.userName.trim() || userRef;
   const organizationId = caller.organizationId.trim();
   const replyTo = payload.email.trim() || userRef;
   if (!host || !token || !userRef || !organizationId || to.length === 0) {
      throw new Error("Job service mail is not configured");
   }

   const response = await (deps.fetch ?? fetch)(`${host}/job/mail/servitut-help`, {
      method: "POST",
      headers: {
         "Content-Type": "application/json",
         Accept: "application/json",
         "access-token": token,
         "user-ref": userRef,
         "user-name": userName,
         "organization-id": organizationId,
      },
      body: JSON.stringify({
         to,
         replyTo,
         subject: formatServitutHelpMailSubject(payload),
         text: formatServitutHelpMailText(payload),
      }),
   });
   if (!response.ok) {
      throw new Error(`Mail request failed (${response.status})`);
   }
}

export function readSessionContact(sessionState: {
   userName?: string;
   userRef?: string;
   userSession?: { getIdToken: () => { payload?: Record<string, unknown> } };
}): { name: string; email: string; phone: string } {
   const payload = sessionState.userSession?.getIdToken()?.payload;
   return {
      name: sessionState.userName?.trim() || readPayloadString(payload, "name"),
      email: sessionState.userRef?.trim() || readPayloadString(payload, "email"),
      phone: readPayloadString(payload, "custom:phone")
         || readPayloadString(payload, "phone_number")
         || readPayloadString(payload, "phone"),
   };
}

function readServitutHelpRecipients(value: unknown): string[] {
   const raw = Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [];
   return [...new Set(raw.map((item) => String(item).trim().toLowerCase()).filter(Boolean))];
}

function formatDatoLoebenr(aktId?: string): string {
   return aktId?.replace(/(.{4})(.{2})(.{2})-()/, "$3$2$1-$4") ?? "";
}

function cadastralFromProperty(data?: Partial<PropertyData>): { matrikelNr: string; ejerlav: string } {
   const nodes = jordstykkerFromProperty(data);
   const current = nodes.filter((node) => !node.status || node.status === "Gældende");
   const source = current.length ? current : nodes;
   const fromGql = joinCadastral(
      source.map((node) => node.matrikelnummer),
      source.map((node) => node.jordstykkeLiggerIEjerlav?.ejerlavsnavn),
   );
   if (fromGql.matrikelNr || fromGql.ejerlav) {
      return fromGql;
   }

   const matrikelNr = data?.Mat2?.matrikelnummer?.toString().trim() || "";
   if (matrikelNr) {
      return {
         matrikelNr,
         ejerlav: ejerlavNameForCode(data, data?.Mat2?.ejerlavskode),
      };
   }

   const sfe = bbrSfeProperties(data);
   return {
      matrikelNr: sfe?.matrikelnummer?.toString().trim() || "",
      ejerlav: sfe?.ejerlavsnavn?.toString().trim() || ejerlavNameForCode(data, sfe?.ejerlavskode),
   };
}

function cadastralFromColumns(
   includedBfes: number[] | undefined,
   columnDetails?: Record<string, ColumnDetail>,
): { matrikelNr: string; ejerlav: string } {
   const matrikler: string[] = [];
   const ejerlav: string[] = [];
   (includedBfes || []).forEach((bfe) => {
      const detail = columnDetails?.[String(bfe)];
      (detail?.matrikler || []).forEach((value) => matrikler.push(value));
      if (detail?.ejerlav) {
         ejerlav.push(detail.ejerlav);
      }
   });
   return joinCadastral(matrikler, ejerlav);
}

function joinCadastral(matrikler: Array<string | undefined>, ejerlav: Array<string | undefined>) {
   return {
      matrikelNr: uniqueText(matrikler).join(", "),
      ejerlav: uniqueText(ejerlav).join(", "),
   };
}

function uniqueText(values: Array<string | undefined>): string[] {
   const seen = new Set<string>();
   const result: string[] = [];
   values.forEach((value) => {
      const text = value?.toString().trim() || "";
      if (!text || seen.has(text)) {
         return;
      }
      seen.add(text);
      result.push(text);
   });
   return result;
}

function jordstykkerFromProperty(data?: Partial<PropertyData>): JordstykkeNode[] {
   const nodes = data?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0]?.jordstykkeSamlesISamletFastEjendom?.nodes;
   return (nodes || []) as JordstykkeNode[];
}

function ejerlavNameForCode(data: Partial<PropertyData> | undefined, code: unknown): string {
   const raw = code?.toString().replace(/\s+/g, "").trim() || "";
   if (!raw || !data?.Ejerlav) {
      return "";
   }
   const numeric = Number(raw);
   const match = data.Ejerlav.find((entry) => Number(entry.kode) === numeric || String(entry.kode) === raw);
   return typeof match?.navn === "string" ? match.navn.trim() : "";
}

function bbrSfeProperties(data?: Partial<PropertyData>): { matrikelnummer?: string; ejerlavsnavn?: string; ejerlavskode?: string | number } | undefined {
   const raw = data?.BBRSFE as unknown;
   const entry = Array.isArray(raw) ? raw[0] : raw;
   const jord = entry?.properties?.jordstykke;
   const properties = Array.isArray(jord) ? jord[0]?.properties : jord?.properties;
   return properties;
}

function readPayloadString(payload: Record<string, unknown> | undefined, key: string): string {
   const value = payload?.[key];
   return typeof value === "string" ? value.trim() : "";
}
